import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { usersApi, type MarathonGroup } from '@/api/users';
import { Button } from '@/components/ui/Button';
import { Card, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Pill } from '@/components/ui/Pill';
import { SearchInput } from '@/components/ui/SearchInput';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { Modal } from '@/components/ui/Modal';
import { ActionSheet } from '@/components/mobile/ActionSheet';
import { MTag } from '@/components/mobile/MobileKit';
import { useIsMobile } from '@/hooks/useIsMobile';
import { humanizeError } from '@/lib/errors';
import { filterEnrollmentRows } from '@/lib/enrollmentActions';

/**
 * Управление группами курса.
 *
 * ⚠️ ОДНА РЕАЛИЗАЦИЯ НА ДВА ЭКРАНА (вынесено 04.09.2026). Панель показывается и
 * на странице курса, и в отдельном разделе «Группы». Копия во втором месте
 * разошлась бы с первой на первой же правке, а заметил бы это администратор,
 * у которого кураторы назначаются по-разному в двух местах.
 */
// GroupsPanel — управление группами внутри марафона: создать/переименовать/
// вместимость/удалить, кураторы (M:N), и «раскидать выбранных учеников в группу».
// Вместимость — мягкий ориентир: превышение подсвечивается, но не блокируется.
export function GroupsPanel({
  marathonId,
  curatorList,
  selectedIds,
  onAssigned,
}: {
  marathonId: string;
  curatorList: { id: string; full_name: string }[];
  selectedIds: string[];
  onAssigned: () => void;
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const ask = useConfirm();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCap, setNewCap] = useState(20);
  const [assignTo, setAssignTo] = useState('');
  const [err, setErr] = useState('');

  const groupsQ = useQuery({
    queryKey: ['admin', 'marathon', marathonId, 'groups'],
    queryFn: () => usersApi.listGroups(marathonId),
  });
  const groups = groupsQ.data?.items ?? [];
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'marathon', marathonId, 'groups'] });
    qc.invalidateQueries({ queryKey: ['admin', 'group'] }); // раскрытый состав групп
  };
  const fail = (e: unknown) => setErr(humanizeError(e));

  const createM = useMutation({
    mutationFn: () => usersApi.createGroup(marathonId, newName.trim(), newCap),
    onSuccess: () => { setErr(''); setNewName(''); setNewCap(20); setCreateOpen(false); invalidate(); },
    onError: fail,
  });
  const updateM = useMutation({
    mutationFn: (v: { id: string; patch: { name?: string; capacity?: number } }) => usersApi.updateGroup(v.id, v.patch),
    onSuccess: () => { setErr(''); invalidate(); }, onError: fail,
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => usersApi.deleteGroup(id),
    onSuccess: () => { setErr(''); invalidate(); onAssigned(); }, onError: fail,
  });
  const addCurM = useMutation({
    mutationFn: (v: { groupId: string; curatorId: string }) => usersApi.addGroupCurator(v.groupId, v.curatorId),
    onSuccess: () => { setErr(''); invalidate(); }, onError: fail,
  });
  const remCurM = useMutation({
    mutationFn: (v: { groupId: string; curatorId: string }) => usersApi.removeGroupCurator(v.groupId, v.curatorId),
    onSuccess: () => { setErr(''); invalidate(); }, onError: fail,
  });
  const assignM = useMutation({
    mutationFn: (groupId: string) => usersApi.assignGroupMembers(groupId, selectedIds),
    onSuccess: () => { setErr(''); setAssignTo(''); invalidate(); onAssigned(); }, onError: fail,
  });

  return (
    <Card>
      <div className="flex items-center justify-between gap-2">
        <CardTitle>{t('adminGroups.title')}</CardTitle>
        <Pill variant="neutral">{t('adminGroups.count', { count: groups.length })}</Pill>
      </div>

      {/* Раскидать выбранных учеников в группу */}
      {selectedIds.length > 0 && groups.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-card bg-primary-container/40 p-3">
          <span className="text-sm font-medium">{t('adminGroups.selected', { n: selectedIds.length })}</span>
          <select
            className="h-10 min-w-[200px] rounded-button bg-surface-container-lowest px-3 text-sm shadow-soft"
            value={assignTo}
            onChange={(e) => setAssignTo(e.target.value)}
          >
            <option value="" disabled>{t('adminGroups.toGroup')}</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <Button size="sm" loading={assignM.isPending} disabled={!assignTo} onClick={() => assignTo && assignM.mutate(assignTo)}>
            {t('adminGroups.assign')}
          </Button>
        </div>
      )}

      {/* Создать группу. На телефоне — кнопкой и окном во весь экран (§5.9, правило 3): три поля в строку на 390 не
          помещались, а «Создать» уезжало под клавиатуру. */}
      {isMobile ? (
        <>
          <Button onClick={() => setCreateOpen(true)} className="mt-3 w-full" data-group-create-phone>
            <span className="material-symbols-outlined">add</span>
            {t('curatorGroups.createTitle')}
          </Button>
          <Modal
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            title={t('curatorGroups.createTitle')}
            size="sm"
            phoneFull
            footer={
              <div className="grid grid-cols-2 gap-2">
                <Button variant="ghost" onClick={() => setCreateOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button loading={createM.isPending} disabled={!newName.trim()} onClick={() => createM.mutate()}>
                  {t('adminGroups.create')}
                </Button>
              </div>
            }
          >
            <div className="space-y-3">
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('adminGroups.namePlaceholder')} />
              <Input
                type="number"
                min={1}
                label={t('adminGroups.capacityShort')}
                value={String(newCap)}
                onChange={(e) => setNewCap(Number(e.target.value) || 20)}
              />
              {err && <p className="text-sm text-error">{err}</p>}
            </div>
          </Modal>
        </>
      ) : (
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="min-w-[200px] flex-1">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('adminGroups.namePlaceholder')} />
        </div>
        <div className="w-28">
          <Input type="number" min={1} value={String(newCap)} onChange={(e) => setNewCap(Number(e.target.value) || 20)} placeholder={t('adminGroups.capacityShort')} />
        </div>
        <Button loading={createM.isPending} disabled={!newName.trim()} onClick={() => createM.mutate()}>
          <span className="material-symbols-outlined">add</span>
          {t('adminGroups.create')}
        </Button>
      </div>
      )}
      {err && <p className="mt-2 text-sm text-error">{err}</p>}

      {/* Список групп */}
      {groups.length === 0 ? (
        <p className="mt-3 text-sm text-on-surface-variant">{t('adminGroups.empty')}</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {groups.map((g) => (
            <GroupRow
              key={g.id}
              phone={isMobile}
              group={g}
              curatorList={curatorList}
              onRename={(name) => updateM.mutate({ id: g.id, patch: { name } })}
              onCapacity={(capacity) => updateM.mutate({ id: g.id, patch: { capacity } })}
              onDelete={async () => { if (await ask({ message: t('adminGroups.deleteConfirm', { name: g.name }), danger: true })) deleteM.mutate(g.id); }}
              onAddCurator={(cid) => addCurM.mutate({ groupId: g.id, curatorId: cid })}
              onRemoveCurator={(cid) => remCurM.mutate({ groupId: g.id, curatorId: cid })}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

// Одна строка-карточка группы: имя (правится по blur), вместимость с
// подсветкой превышения, кураторы-чипсы, кнопка удаления.
function GroupRow({
  phone = false,
  group,
  curatorList,
  onRename,
  onCapacity,
  onDelete,
  onAddCurator,
  onRemoveCurator,
}: {
  /** Телефон: название и места — текстом, правка — листом «⋯» и окном (§5.9), а не полями прямо в строке. */
  phone?: boolean;
  group: MarathonGroup;
  curatorList: { id: string; full_name: string }[];
  onRename: (name: string) => void;
  onCapacity: (capacity: number) => void;
  onDelete: () => void;
  onAddCurator: (curatorId: string) => void;
  onRemoveCurator: (curatorId: string) => void;
}) {
  const { t } = useTranslation();
  const over = group.member_count > group.capacity;
  const inGroup = new Set(group.curators.map((c) => c.id));
  const addable = curatorList.filter((c) => !inGroup.has(c.id));
  const [sheet, setSheet] = useState(false);
  const [edit, setEdit] = useState<'name' | 'capacity' | null>(null);
  return (
    <li className="rounded-card border border-outline-variant/60 p-3">
      {phone ? (
        <div className="flex items-center gap-2" data-group-row-phone>
          <p className="min-w-0 flex-1 text-[14px] font-semibold leading-snug text-on-surface">{group.name}</p>
          <MTag tone={over ? 'warm' : 'muted'}>{group.member_count} / {group.capacity}</MTag>
          <button
            type="button"
            onClick={() => setSheet(true)}
            aria-label={t('nav.more')}
            className="grid h-11 w-11 flex-none place-items-center rounded-full text-on-surface-variant"
          >
            <span className="material-symbols-outlined text-[20px]">more_horiz</span>
          </button>
          <ActionSheet
            open={sheet}
            onClose={() => setSheet(false)}
            title={group.name}
            actions={[
              { label: t('staffMobile.groupRename'), icon: 'edit', onSelect: () => setEdit('name') },
              { label: t('staffMobile.groupCapacity'), icon: 'groups', onSelect: () => setEdit('capacity') },
              { label: t('adminGroups.deleteGroup'), icon: 'delete', danger: true, onSelect: onDelete },
            ]}
          />
          {edit && (
            <GroupFieldModal
              kind={edit}
              group={group}
              onClose={() => setEdit(null)}
              onSave={(v) => {
                if (edit === 'name' && v.trim() && v.trim() !== group.name) onRename(v.trim());
                if (edit === 'capacity' && Number(v) >= 1 && Number(v) !== group.capacity) onCapacity(Number(v));
              }}
            />
          )}
        </div>
      ) : (
      <div className="flex flex-wrap items-center gap-2">
        <input
          defaultValue={group.name}
          onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== group.name) onRename(v); }}
          className="min-w-[160px] flex-1 rounded-button bg-surface-container-lowest px-3 py-2 text-sm font-semibold shadow-soft"
        />
        <label className="flex items-center gap-1 text-xs text-on-surface-variant">
          {t('adminGroups.capacityLabel')}
          <input
            type="number"
            min={1}
            defaultValue={group.capacity}
            onBlur={(e) => { const v = Number(e.target.value) || group.capacity; if (v !== group.capacity) onCapacity(v); }}
            className="w-16 rounded-button bg-surface-container-lowest px-2 py-1 text-sm shadow-soft"
          />
        </label>
        <Pill variant={over ? 'warning' : 'success'}>{group.member_count} / {group.capacity}</Pill>
        <button type="button" onClick={onDelete} title={t('adminGroups.deleteGroup')} className="rounded-button p-1 text-on-surface-variant hover:text-error">
          <span className="material-symbols-outlined text-base">delete</span>
        </button>
      </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {group.curators.length === 0 && <span className="text-xs text-on-surface-variant">{t('adminGroups.noCurators')}</span>}
        {group.curators.map((c) => (
          <span key={c.id} className="inline-flex items-center gap-1 rounded-pill bg-primary-container px-2.5 py-1 text-xs font-medium text-on-primary-container">
            {c.name || t('adminGroups.curator')}
            <button type="button" onClick={() => onRemoveCurator(c.id)} title={t('adminGroups.remove')} className="hover:text-error">
              <span className="material-symbols-outlined text-sm">close</span>
            </button>
          </span>
        ))}
        {addable.length > 0 && (
          <select
            className="h-8 rounded-button bg-surface-container-lowest px-2 text-xs shadow-soft"
            value=""
            onChange={(e) => { if (e.target.value) onAddCurator(e.target.value); }}
          >
            <option value="">{t('adminGroups.addCurator')}</option>
            {addable.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
          </select>
        )}
      </div>
      <GroupMembers groupId={group.id} count={group.member_count} />
    </li>
  );
}

/** Телефон: «Переименовать» и «Места» из листа «⋯» группы — окном во весь экран, кнопки внизу (правило 3). */
function GroupFieldModal({
  kind,
  group,
  onClose,
  onSave,
}: {
  kind: 'name' | 'capacity';
  group: MarathonGroup;
  onClose: () => void;
  onSave: (value: string) => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(kind === 'name' ? group.name : String(group.capacity));
  const ok = kind === 'name' ? value.trim() !== '' : Number(value) >= 1;
  return (
    <Modal
      open
      onClose={onClose}
      title={kind === 'name' ? t('staffMobile.groupRename') : t('staffMobile.groupCapacity')}
      size="sm"
      phoneFull
      footer={
        <div className="grid grid-cols-2 gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!ok}
            onClick={() => {
              onSave(value);
              onClose();
            }}
          >
            {t('common.save')}
          </Button>
        </div>
      }
    >
      <Input
        type={kind === 'capacity' ? 'number' : 'text'}
        min={kind === 'capacity' ? 1 : undefined}
        label={kind === 'capacity' ? t('adminGroups.capacityShort') : undefined}
        placeholder={kind === 'name' ? t('adminGroups.namePlaceholder') : undefined}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
    </Modal>
  );
}

/**
 * Состав группы по раскрытию, с поиском по имени и почте (28.09.2026, вторая волна админки п. 11). Раньше панель
 * показывала только «N / мест»: кто в группе, было не узнать, не выходя в список учеников курса. Состав
 * запрашивается только при раскрытии — групп на курсе бывает десяток.
 */
function GroupMembers({ groupId, count }: { groupId: string; count: number }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const members = useQuery({
    queryKey: ['admin', 'group', groupId, 'members'],
    queryFn: () => usersApi.listGroupMembers(groupId),
    enabled: open,
  });
  const all = members.data?.items ?? [];
  const shown = filterEnrollmentRows(all, q);
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen((v) => !v)} className="text-xs font-semibold text-primary">
        {open ? t('adminGroups.hideMembers') : t('adminGroups.showMembers', { count })}
      </button>
      {open && (
        <div className="mt-2">
          {members.isLoading ? (
            <p className="text-xs text-on-surface-variant">{t('common.loading')}</p>
          ) : members.isError ? (
            <p className="text-xs text-error">{humanizeError(members.error)}</p>
          ) : (
            <>
              <SearchInput value={q} placeholder={t('adminGroups.membersSearch')} onSearch={setQ} />
              <p className="mt-1 text-xs text-on-surface-variant">
                {t('common.shownOf', { shown: shown.length, total: all.length })}
              </p>
              {/* Своя прокрутка — только на компьютере: на телефоне вложенная прокрутка ловит палец. */}
              <ul className="mt-1 divide-y divide-outline-variant/40 text-sm md:max-h-72 md:overflow-y-auto">
                {shown.map((m) => (
                  <li key={m.id} className="py-1.5">
                    <span className="font-medium">{m.student_name || m.student_id.slice(0, 8)}</span>
                    {m.student_email && <span className="ml-2 text-xs text-on-surface-variant">{m.student_email}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
